/** A tall rock wall framing one side of the view, with strata and a mossy cap. */
import type { CliffsLayer } from '@dropstack/core';
import type { SceneSize } from '../layout.ts';
import { hash01, valueNoise } from '../noise.ts';
import { circleBlob, rgba, rimOf, shade, smoothLine, strokeRim, vGradient, type Circle, type Ctx, type Point } from './draw.ts';
import type { Painter } from './types.ts';

interface CliffShape {
  /** Outline: outer edge → top → face → bottom (closed). */
  outline: Point[];
  /** Just the exposed face, top to bottom. */
  face: Point[];
  /** Just the top edge, from the outer side to the face corner. */
  top: Point[];
  /** +1 when the face looks right (left cliff), −1 when it looks left. */
  dir: 1 | -1;
}

/** Deterministic cliff outline in CSS px. */
export function cliffShape(layer: CliffsLayer, size: SceneSize, seed: number): CliffShape {
  const { width, height, unit, bleed } = size;
  const dir: 1 | -1 = layer.side === 'left' ? 1 : -1;
  const outer = layer.side === 'left' ? -bleed - 4 : width + bleed + 4;
  const reach = layer.width * width;
  const topY = layer.top * height;
  // Face x (as distance from the outer screen edge) for a given y: ledges and bulges.
  const faceAt = (y: number) => {
    const t = (y - topY) / unit;
    const bulge = 0.09 * valueNoise(seed, t * 3.1) + 0.05 * valueNoise(seed + 1, t * 8.3);
    const ledge = Math.floor(t / 0.16);
    const step = (hash01(seed + 2, ledge) - 0.5) * 0.08;
    return reach * (0.9 + bulge + step);
  };
  const xOf = (d: number) => (layer.side === 'left' ? d : width - d);
  const face: Point[] = [];
  const stepY = Math.max(4, unit * 0.012);
  for (let y = topY + unit * 0.03; y <= height + stepY; y += stepY) face.push({ x: xOf(faceAt(y)), y: Math.min(y, height + 2) });
  const corner = faceAt(topY + unit * 0.03);
  const top: Point[] = [];
  const topSteps = 10;
  for (let i = 0; i <= topSteps; i++) {
    const t = i / topSteps;
    const d = -bleed - 4 + (corner + bleed + 4) * t;
    // Gentle rounded crown that rolls over into the face.
    const roll = t > 0.8 ? ((t - 0.8) / 0.2) ** 2 * unit * 0.03 : 0;
    top.push({ x: xOf(d), y: topY + roll - unit * 0.008 * valueNoise(seed + 3, d / unit / 0.08) });
  }
  const outline = [{ x: outer, y: height + 2 }, ...top, ...face, { x: outer, y: height + 2 }];
  return { outline, face, top, dir };
}

function tufts(ctx: Ctx, at: readonly Point[], r: number, color: string, size: SceneSize): void {
  const circles: Circle[] = [];
  for (const p of at) circles.push({ x: p.x - r * 0.6, y: p.y, r: r * 0.7 }, { x: p.x, y: p.y - r * 0.35, r }, { x: p.x + r * 0.6, y: p.y, r: r * 0.7 });
  circleBlob(ctx, circles, color, rimOf(color), size, 0.7);
}

export const cliffsPainter: Painter<CliffsLayer> = {
  extent: (layer, { size }) => ({ top: layer.top * size.height - size.unit * 0.06, bottom: size.height }),
  still(ctx, layer, { size, seed }) {
    const shape = cliffShape(layer, size, seed);
    const topY = layer.top * size.height;
    const rim = layer.rim ?? rimOf(layer.color);

    ctx.beginPath();
    smoothLine(ctx, shape.outline);
    ctx.closePath();
    ctx.fillStyle = vGradient(ctx, topY, size.height, [shade(layer.color, 0.1), layer.color, shade(layer.color, -0.06)]);
    ctx.fill();

    ctx.save();
    ctx.clip();
    // Strata: gently wavy horizontal bands.
    if (layer.stripes) {
      ctx.strokeStyle = layer.stripes;
      ctx.lineWidth = Math.max(1.5, size.unit * 0.009);
      ctx.lineCap = 'round';
      const gap = size.unit * 0.055;
      for (let k = 0, y = topY + gap * 0.8; y < size.height; k++, y += gap * (0.75 + 0.5 * hash01(seed + 7, k))) {
        const pts: Point[] = [];
        for (let x = -size.bleed; x <= size.width + size.bleed; x += size.unit * 0.04) {
          pts.push({ x, y: y + size.unit * 0.008 * valueNoise(seed + 9 + k, x / size.unit / 0.1) });
        }
        ctx.beginPath();
        smoothLine(ctx, pts);
        ctx.stroke();
      }
    }
    // Shade along the face so the wall reads as rounded rock, not a flat cut-out.
    ctx.beginPath();
    smoothLine(ctx, shape.face);
    ctx.strokeStyle = rgba(shade(layer.color, -0.25), 0.35);
    ctx.lineWidth = size.unit * 0.05;
    ctx.stroke();
    ctx.restore();

    ctx.beginPath();
    smoothLine(ctx, [...shape.top, ...shape.face]);
    strokeRim(ctx, rim, size);

    if (!layer.moss) return;
    const moss = layer.moss;
    // Grassy cap rolling over the top edge.
    const capR = size.unit * 0.022;
    const cap: Circle[] = shape.top.map((p, i) => ({ x: p.x, y: p.y + capR * 0.15, r: capR * (0.9 + 0.35 * hash01(seed + 21, i)) }));
    const corner = shape.top[shape.top.length - 1];
    if (corner) cap.push({ x: corner.x + shape.dir * capR * 0.3, y: corner.y + capR * 0.5, r: capR * 0.9 });
    // Hanging moss strands below the cap near the face.
    ctx.strokeStyle = shade(moss, -0.12);
    ctx.lineCap = 'round';
    ctx.lineWidth = Math.max(1.5, size.unit * 0.006);
    for (let i = 0; i < 5; i++) {
      const p = shape.face[Math.min(shape.face.length - 1, i * 2)];
      if (!p) break;
      const len = size.unit * (0.03 + 0.05 * hash01(seed + 23, i));
      const x = p.x - shape.dir * size.unit * (0.01 + 0.03 * i);
      ctx.beginPath();
      ctx.moveTo(x, topY);
      ctx.quadraticCurveTo(x + shape.dir * len * 0.3, topY + len * 0.6, x, topY + len);
      ctx.stroke();
    }
    const capBlobs = cap.map((c) => ({ ...c }));
    circleBlob(ctx, capBlobs, vGradient(ctx, topY - capR, topY + capR, [shade(moss, 0.18), moss]), rimOf(moss), size);
    // Tufts clinging to ledges on the face.
    const ledges = shape.face.filter((_, i) => i > 3 && hash01(seed + 25, i) > 0.9).slice(0, 4);
    tufts(ctx, ledges, size.unit * 0.013, moss, size);
  },
};
